/* eslint global-require: 0 */
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { Route, Switch, withRouter } from 'react-router-dom';
import compose from 'recompose/compose';
import lifecycle from 'recompose/lifecycle';
import withProps from 'recompose/withProps';
import { connect } from 'react-redux';
import sEmitter from '@gorangajic/storage-emitter';
import { Fill, Provider, Slot } from 'react-slot-fill';
import debounce from 'lodash/debounce';
import { isString } from 'lodash';
import './styles';
import FullPageLoading from './Components/FullPageLoading';
import Alert from './Components/Alert';
import PromptModal from './Components/PromptModal';
import refreshPage from './hoc/refreshPage';
import Snackbar from './Components/Snackbar';
import PositionTypeMissing from './Components/PositionTypeMissing';
import ExtraUserInformation from './Components/ExtraUserInformation';
import ExtraUserInformationLakeCounty from './Components/ExtraUserInformationLakeCounty';
import ExtraUserInformationNwtc from './Components/ExtraUserInformationLakeCounty/ExtraUserInformation';
import MoreInfoPage from './Components/ExtraUserInformation/MoreInfoPage';
import MergeRequestModal from './Components/MergeRequestModal';
import NewPersonalityAssessmentModal from './Components/NewPersonalityAssessmentModal';
import TermsAndConditionsNotification from './Components/TermsAndConditionsNotification';
import ConnectWithCollegeModal from './Components/ConnectWithCollegeModal';
import UbeuLeadIdScript from './Components/UbeuLeadId/UbeuLeadIdScript';
import GoogleAnalytics from './Components/GoogleAnalytics';
import MaintenanceNotification from './Components/MaintenanceNotification';
import SupportWidget from './Components/SupportWidget';
import loginRequired from './hoc/loginRequired';
import guestPage from './hoc/guestPage';
import routeLoader from './hoc/routeLoader';
import redirectTo from './hoc/redirectTo';
import { changeViewAction, COUNSELOR_VIEW, STUDENT_VIEW, syncLoggedInUserAction } from './redux/app';
import { getBannerFlagAction, getBannerMessageAction, isAuthenticated } from './redux/auth';
import AuthLayout from './Layouts/AuthLayout';
import DashboardLayout from './Layouts/DashboardLayout';
import isWorkForce from './utils/isWorkForce';
import './utils/hide-outline';
import SITE_MODES from '../constants/site-modes';
import spinnerWhileLoading from './hoc/spinnerWhileLoading';
import { setWindowDimensionsAction } from './redux/window-resize';
import { setPGHomeLinkAction } from './redux/pg-home-link';
import LogRocketEvent from './services/LogRocket';
import { identify as mixpanelIdentify } from './services/Mixpanel';


const studentRoutes = ['/assessment', '/journey'];

const loadRoute = routeLoader(FullPageLoading);

const Login = loadRoute((done) => {
    import('./Pages/Auth/Login').then((Page) => {
      done(guestPage(Page.default));
    })
      .catch(err => done(refreshPage(err)));
});

const SignUp = loadRoute((done) => {
    import('./Pages/Auth/SignUp').then((Page) => {
      done(guestPage(Page.default));
    })
      .catch(err => done(refreshPage(err)));
});

const ProcessPayment = loadRoute((done) => {
    import('./Pages/Auth/ProcessPayment').then((Page) => {
      done(loginRequired(Page.default));
    })
      .catch(err => done(refreshPage(err)));
});

const ConfirmEmail = loadRoute((done) => {
    import('./Pages/Auth/ConfirmEmail').then((Page) => {
      done(guestPage(Page.default));
    })
      .catch(err => done(refreshPage(err)));
});

const ActivateUser = loadRoute((done) => {
    import('./Pages/Auth/ActivateUser').then((Page) => {
      done(Page.default);
    })
      .catch(err => done(refreshPage(err)));
});

const ForgetPassword = loadRoute((done) => {
    import('./Pages/Auth/ForgetPassword').then((Page) => {
      done(guestPage(Page.default));
    })
      .catch(err => done(refreshPage(err)));
});

const ResetPassword = loadRoute((done) => {
    import('./Pages/Auth/ResetPassword').then((Page) => {
      done(Page.default);
    })
      .catch(err => done(refreshPage(err)));
});

const Connect = loadRoute((done) => {
    import('./Pages/Auth/Connect').then((Page) => {
      done(guestPage(Page.default));
    })
      .catch(err => done(refreshPage(err)));
});

const ConnectVerify = loadRoute((done) => {
    import('./Pages/Auth/ConnectVerify').then((Page) => {
      done(Page.default);
    })
      .catch(err => done(refreshPage(err)));
});

const ConfirmConnect = loadRoute((done) => {
    import('./Pages/Auth/ConfirmConnect').then((Page) => {
      done(guestPage(Page.default));
    })
      .catch(err => done(refreshPage(err)));
});

const Logout = loadRoute((done) => {
    import('./Pages/Auth/Logout').then((Page) => {
      done(Page.default);
    })
      .catch(err => done(refreshPage(err)));
});

const Resources = loadRoute((done) => {
    import('./Pages/Dashboard/Resources').then((Page) => {
      done(loginRequired(Page.default));
    })
      .catch(err => done(refreshPage(err)));
});

const Resource = loadRoute((done) => {
    import('./Pages/Dashboard/Resource').then((Page) => {
      done(loginRequired(Page.default));
    })
      .catch(err => done(refreshPage(err)));
});

const ToolPosts = loadRoute((done) => {
    import('./Pages/Dashboard/ToolPosts').then((Page) => {
      done(loginRequired(Page.default));
    })
      .catch(err => done(refreshPage(err)));
});

const ToolCategories = loadRoute((done) => {
    import('./Pages/Dashboard/ToolCategories').then((Page) => {
      done(loginRequired(Page.default));
    })
      .catch(err => done(refreshPage(err)));
});

const Tool = loadRoute((done) => {
    import('./Pages/Dashboard/Tool').then((Page) => {
      done(loginRequired(Page.default));
    })
      .catch(err => done(refreshPage(err)));
});

const Students = loadRoute((done) => {
    import('./Pages/Dashboard/Students').then((Page) => {
      done(loginRequired(Page.default));
    })
      .catch(err => done(refreshPage(err)));
});

const Candidates = loadRoute((done) => {
    import('./Pages/Dashboard/Candidates').then((Page) => {
      done(loginRequired(Page.default));
    })
      .catch(err => done(refreshPage(err)));
});

const Employees = loadRoute((done) => {
    import('./Pages/Dashboard/Employees').then((Page) => {
      done(loginRequired(Page.default));
    })
      .catch(err => done(refreshPage(err)));
});

const CandidateResult = loadRoute((done) => {
    import('./Pages/Dashboard/CandidateResult').then((Page) => {
      done(loginRequired(Page.default));
    })
      .catch(err => done(refreshPage(err)));
});

const Assessment = loadRoute((done) => {
    import('./Pages/Dashboard/Assessment').then((Page) => {
      done(loginRequired(Page.default));
    })
      .catch(err => done(refreshPage(err)));
});

const StudentResultPage = loadRoute((done) => {
    import('./Pages/Dashboard/StudentResultPage').then((Page) => {
      done(loginRequired(Page.default));
    })
      .catch(err => done(refreshPage(err)));
});

const StudentJourney = loadRoute((done) => {
    import('./Pages/Dashboard/StudentJourney').then((Page) => {
      done(loginRequired(Page.default));
    })
      .catch(err => done(refreshPage(err)));
});

const ManageJourneyCards = loadRoute((done) => {
    import('./Pages/Admin/ManageJourneyCards').then((Page) => {
      done(loginRequired(Page.default));
    })
      .catch(err => done(refreshPage(err)));
});

const AddEditJourneyCard = loadRoute((done) => {
    import('./Pages/Admin/AddEditJourneyCard').then((Page) => {
      done(loginRequired(Page.default));
    })
      .catch(err => done(refreshPage(err)));
});

const CareerMatches = loadRoute((done) => {
    import('./Pages/Dashboard/CareerMatches').then((Page) => {
      done(loginRequired(Page.default));
    })
      .catch(err => done(refreshPage(err)));
});

const ProgramMatches = loadRoute((done) => {
    import('./Pages/Dashboard/ProgramsMatches').then((Page) => {
      done(loginRequired(Page.default));
    })
      .catch(err => done(refreshPage(err)));
});


// const PetersonsExtraInfo = loadRoute((done) => {
//     import('./Pages/Dashboard/PetersonsExtraInfo').then((Page) => {
//         done(loginRequired(Page.default));
//     }).catch(err => done(refreshPage(err)));
// });

const CareerDetails = loadRoute((done) => {
    import('./Pages/Dashboard/CareerDetails').then((Page) => {
      done(loginRequired(Page.default));
    })
      .catch(err => done(refreshPage(err)));
});

const ProgramDetails = loadRoute((done) => {
    import('./Pages/Dashboard/ProgramDetails').then((Page) => {
      done(loginRequired(Page.default));
    })
      .catch(err => done(refreshPage(err)));
});

const CareerMatchesRedirect = loadRoute((done) => {
    import('./Pages/Dashboard/CareerMatchesRedirect').then((Page) => {
      done(loginRequired(Page.default));
    })
      .catch(err => done(refreshPage(err)));
});

const ProgramMatchesRedirect = loadRoute((done) => {
    import('./Pages/Dashboard/ProgramMatchesRedirect').then((Page) => {
      done(loginRequired(Page.default));
    })
      .catch(err => done(refreshPage(err)));
});

const CareerDetailsRedirect = loadRoute((done) => {
    import('./Pages/Dashboard/CareerDetailsRedirect').then((Page) => {
      done(loginRequired(Page.default));
    })
      .catch(err => done(refreshPage(err)));
});

const ProgramDetailsRedirect = loadRoute((done) => {
    import('./Pages/Dashboard/ProgramDetailsRedirect').then((Page) => {
      done(loginRequired(Page.default));
    })
      .catch(err => done(refreshPage(err)));
});

const AccountSettings = loadRoute((done) => {
    import('./Pages/Dashboard/AccountSettings').then((Page) => {
      done(loginRequired(Page.default));
    })
      .catch(err => done(refreshPage(err)));
});

const Intro = loadRoute((done) => {
    import('./Pages/Intro/OnboardingIntro').then((Page) => {
      done(Page.default);
    })
      .catch(err => done(refreshPage(err)));
});

const Page404 = loadRoute((done) => {
    import('./Pages/Page404').then((Page) => {
      done(Page.default);
    })
      .catch(err => done(refreshPage(err)));
});

const ErrorPage = loadRoute((done) => {
    import('./Pages/ErrorPage').then((Page) => {
      done(Page.default);
    })
      .catch(err => done(refreshPage(err)));
});

const ChangeView = loadRoute((done) => {
    import('./Pages/ChangeView').then((Page) => {
      done(Page.default);
    })
      .catch(err => done(refreshPage(err)));
});

const Jobs = loadRoute((done) => {
    import('./Pages/Dashboard/Jobs').then((Page) => {
      done(loginRequired(Page.default));
    })
      .catch(err => done(refreshPage(err)));
});

const MilitaryCrosswalk = loadRoute((done) => {
    import('./Pages/Dashboard/MilitaryCrosswalk').then((Page) => {
      done(loginRequired(Page.default));
    })
      .catch(err => done(refreshPage(err)));
});

const AdminUsersList = loadRoute((done) => {
    import('./Pages/Admin/AdminUsersList').then((Page) => {
      done(loginRequired(Page.default));
    })
      .catch(err => done(refreshPage(err)));
});

const ProgramsAndSchools = loadRoute((done) => {
    import('./Pages/Admin/ManageProgramsAndSchools').then((Page) => {
      done(loginRequired(Page.default));
    })
      .catch(err => done(refreshPage(err)));
});

const ManageSites = loadRoute((done) => {
    import('./Pages/Admin/ManageSites').then((Page) => {
      done(loginRequired(Page.default));
    })
      .catch(err => done(refreshPage(err)));
});

const CreateEditCompany = loadRoute((done) => {
    import('./Pages/Admin/CreateEditCompany').then((Page) => {
      done(loginRequired(Page.default));
    })
      .catch(err => done(refreshPage(err)));
});

const CreateCompanyOnboarding = loadRoute((done) => {
    import('./Pages/Admin/CreateCompanyForm').then((Page) => {
      done(guestPage(Page.default));
    })
      .catch(err => done(refreshPage(err)));
});

const CreateEditCompanyPosition = loadRoute((done) => {
    import('./Pages/Admin/CreateEditCompanyPosition').then((Page) => {
      done(loginRequired(Page.default));
    })
      .catch(err => done(refreshPage(err)));
});

const ManageUserPosition = loadRoute((done) => {
    import('./Pages/Dashboard/ManageUserPosition').then((Page) => {
      done(loginRequired(Page.default));
    })
      .catch(err => done(refreshPage(err)));
});


const UploadUsersBerry = loadRoute((done) => {
    import('./Pages/Admin/UploadStudents').then((Page) => {
      done(loginRequired(Page.default));
    })
      .catch(err => done(refreshPage(err)));
});

const Colleges = loadRoute((done) => {
    import('./Pages/Dashboard/Colleges').then((Page) => {
      done(loginRequired(Page.default));
    })
      .catch(err => done(refreshPage(err)));
});

const UniversalLogin = loadRoute((done) => {
    import('./Pages/Dashboard/UniversalLogin').then((Page) => {
      done(loginRequired(Page.default));
    })
      .catch(err => done(refreshPage(err)));
});

const MergeAccount = loadRoute((done) => {
    import('./Pages/Auth/MergeAccount').then((Page) => {
      done(Page.default);
    })
      .catch(err => done(refreshPage(err)));
});

const ManagePrograms = loadRoute((done) => {
    import('./Pages/Admin/ManagePrograms').then((Page) => {
      done(Page.default);
    })
      .catch(err => done(refreshPage(err)));
});

const ManageProgramsCustom = loadRoute((done) => {
    import('./Pages/Admin/ManageProgramsCustom').then((Page) => {
      done(Page.default);
    })
      .catch(err => done(refreshPage(err)));
});

const ApiDocs = loadRoute((done) => {
    import('./Pages/ApiDocs').then((Page) => {
      done(Page.default);
    })
      .catch(err => done(refreshPage(err)));
});

const Intake = loadRoute((done) => {
    import('./Pages/Dashboard/IntakePage').then((Page) => {
      done(loginRequired(Page.default));
    })
      .catch(err => done(refreshPage(err)));
});

const DemographicProfilePage = loadRoute((done) => {
    import('./Pages/Dashboard/DemographicProfilePage').then((Page) => {
      done(loginRequired(Page.default));
    })
      .catch(err => done(refreshPage(err)));
});

const StudentMyResultPage = connect(({ me }) => ({
  id: me && me.userId,
  basePath: '/results',
}))(StudentResultPage);

const ResultPage = withProps(({ match: { params: { id } } }) => ({
  id,
  basePath: `/result/${id}`,
}))(StudentResultPage);

export const App = ({
  view,
  jobBoard,
  customPrograms,
  level,
  terms,
  showMyJourney,
  siteMode,
  companyDomain,
  showBanner,
  connectMode,
  setWindowDimensions,
  isLoggedIn,
  companyId,
  user,
}) => {
  let Dashboard;
  let dashboardTitle = '';

  useEffect(() => {
    const debouncedHandleResize = debounce(() => {
      setWindowDimensions({
        height: window.innerHeight,
        width: window.innerWidth,
        orientation: window.orientation,
      });
    }, 100);

    window.addEventListener('resize', debouncedHandleResize);

    return () => {
      window.removeEventListener('resize', debouncedHandleResize);
    };
  }, []);

  useEffect(() => {
    if (isLoggedIn && user) {
      const pgEmailTester = /@peoplegrove\.com$/;
      const { emailAddress, userId } = user;
      mixpanelIdentify({ companyId, userId });
      if (isString(emailAddress) && !pgEmailTester.test(emailAddress)) {
        LogRocketEvent.initialize();
        setTimeout(() => {
          LogRocketEvent.identify({ companyId, userId });
        }, 100);
      }
    }
  }, [isLoggedIn]);

  if (companyDomain === 'login') {
    return (<Provider>
      <Switch>
        <AuthLayout
          path="/login"
          title="Login"
          exact
          component={Login}
        />
        <AuthLayout
          path="/forget-password"
          title="Forgot Password"
          component={ForgetPassword}
        />
        <AuthLayout
          path="/reset-password"
          title="Reset Password"
          component={ResetPassword}
        />
        <Route path="/logout" component={Logout} />
        <AuthLayout
          path="/"
          exact
          component={UniversalLogin}
        />
        <DashboardLayout
          component={Page404}
          title="Not Found"
        />
      </Switch>
      {/* <Intercom /> */}
      <Alert />
      <PromptModal />
      <Snackbar />
    </Provider>);
  }
  if (view === STUDENT_VIEW) {
    const showResults = (!showMyJourney ||
            siteMode === SITE_MODES.EMPLOYER ||
            connectMode
    );
    Dashboard = redirectTo(showResults ? '/results' : '/journey');
  } else {
    Dashboard = siteMode === SITE_MODES.EMPLOYER ? Candidates : Students;
    if (siteMode === SITE_MODES.EMPLOYER) {
      dashboardTitle = 'Candidates';
    } else {
      dashboardTitle = isWorkForce(terms) ? 'Users' : 'Students';
    }
  }
  return (<Provider>
    <Switch>
      <AuthLayout
        path="/login"
        title="Login"
        component={Login}
      />
      <AuthLayout
        path="/signup"
        title="SignUp"
        component={SignUp}
      />
      <AuthLayout
        path="/onboarding"
        title="Onboarding"
        component={CreateCompanyOnboarding}
      />
      <AuthLayout
        path="/process-payment"
        title="Process Payment"
        component={ProcessPayment}
      />
      <AuthLayout
        path="/confirm-email"
        title="Confirm Your Email"
        component={ConfirmEmail}
      />
      <AuthLayout
        path="/activate-user/:token"
        title="Activate User"
        component={ActivateUser}
      />
      <AuthLayout
        path="/forget-password"
        title="Forgot Password"
        component={ForgetPassword}
      />
      <AuthLayout
        path="/reset-password"
        title="Reset Password"
        component={ResetPassword}
      />
      <AuthLayout
        path="/connect"
        title="Connect Account"
        component={Connect}
      />
      <AuthLayout
        path="/connect-verify/:connect"
        title="Connect Verify"
        component={ConnectVerify}
      />
      <AuthLayout
        path="/confirm-connect"
        title="Confirm Your Email"
        component={ConfirmConnect}
      />
      <AuthLayout
        path="/merge-account/:token"
        title="Merge Account"
        component={MergeAccount}
      />
      <Route path="/logout" component={Logout} />
      <DashboardLayout
        path="/resources"
        exact
        component={Resources}
        title="Resources"
      />
      <DashboardLayout
        path="/welcome"
        title="Welcome"
        component={Intake}
      />
      <DashboardLayout
        path="/employees"
        exact
        component={Employees}
        title="Employees"
      />
      <DashboardLayout
        path="/resources/:category/:post"
        exact
        component={Resource}
      />
      <DashboardLayout
        path="/tools/:category/:post"
        exact
        component={Tool}
      />
      <DashboardLayout
        path="/tools/:category"
        exact
        component={ToolPosts}
      />
      <DashboardLayout
        path="/tools"
        exact
        component={ToolCategories}
        title="Tools"
      />
      <DashboardLayout
        path="/profile/:section?"
        exact
        component={DemographicProfilePage}
        title="Profile"
      />
      <DashboardLayout
        path="/results/:type(interests|values|personality|workplace)?"
        component={StudentMyResultPage}
        handleScroll="mobile"
      />
      <DashboardLayout
        path="/candidate/:userId/:positionId"
        component={CandidateResult}
        handleScroll={false}
      />
      <DashboardLayout
        path="/edit-user-position/:userId"
        component={ManageUserPosition}
        handleScroll={false}
      />
      {level <= 2 ? <DashboardLayout
        path="/users"
        component={AdminUsersList}
        title="Administrator"
      /> : null}
      {level <= 2 ? <DashboardLayout
        exact
        path="/sites"
        component={ManageSites}
        title="Administrator"
      /> : null}
      {level <= 2 ? <DashboardLayout
        exact
        path="/sites/:id"
        component={CreateEditCompany}
        title="Manage Company"
      /> : null}
      {level === 1 ? <DashboardLayout
        exact
        path="/sites/:siteId/position/:positionId"
        component={CreateEditCompanyPosition}
        title="Company Positions"
      /> : null}
      {level === 1 && !customPrograms ? <DashboardLayout
        exact
        path="/manage-programs"
        component={ManagePrograms}
        title="Manage Programs"
        handleScroll={false}
      /> : null}
      {level === 1 && !customPrograms ? <DashboardLayout
        exact
        path="/manage-programs-custom"
        component={ManageProgramsCustom}
        title="Manage Programs"
        handleScroll={false}
      /> : null}
      {level === 1 && !customPrograms ? <DashboardLayout
        exact
        path="/upload-users"
        component={UploadUsersBerry}
        title="UploadUsers"
        handleScroll={false}
      /> : null}
      {level <= 2 && customPrograms ? <DashboardLayout
        path="/programs-and-schools"
        component={ProgramsAndSchools}
        title="Manage Programs And Schools"
      /> : null}
      <DashboardLayout
        path="/result/:id/:type(interests|values|personality|workplace)?"
        component={ResultPage}
        handleScroll="mobile"
      />
      <DashboardLayout
        title="Account Settings"
        showBackground
        path="/account"
        component={AccountSettings}
      />
      <DashboardLayout
        path="/journey/(:userId)?"
        title="My Journey"
        component={StudentJourney}
      />
      {level <= 3 ? <DashboardLayout
        path="/manage-journey"
        title="My Journey Cards"
        exact
        component={ManageJourneyCards}
      /> : null}
      {level <= 3 ? <DashboardLayout
        path="/manage-journey/:id"
        component={AddEditJourneyCard}
      /> : null}
      <DashboardLayout
        title={dashboardTitle}
        path="/"
        exact
        component={Dashboard}
      />
      <Route
        path="/career-matches/user/:userId"
        component={CareerMatchesRedirect}
      />
      {customPrograms ? <Route
        path="/program-matches/user/:userId"
        component={ProgramMatchesRedirect}
      /> : null}
      <Route
        path="/career-details/user/:userId/match/:onetId/:occupationId/:detail?"
        component={CareerDetailsRedirect}
      />
      <Route
        path="/program-details/programId/:programId?"
        component={ProgramDetailsRedirect}
      />
      <DashboardLayout
        title="Career Matches"
        path="/career-matches/:area?"
        component={CareerMatches}
      />
      {customPrograms ? <DashboardLayout
        title="Programs Matches"
        path="/program-matches/:area?"
        component={ProgramMatches}
      /> : null}
      <DashboardLayout
        title="Career"
        path="/career-details/:detail?"
        component={CareerDetails}
        handleScroll={false}
      />
      <DashboardLayout
        title="Program"
        path="/program-details/:detail?"
        component={ProgramDetails}
        handleScroll={false}
      />
      <DashboardLayout
        showBackground
        path="/assessment"
        component={Assessment}
      />
      {connectMode ? <DashboardLayout
        title="Connect"
        path="/colleges"
        component={Colleges}
      /> : null}
      {jobBoard ? <DashboardLayout
        title="Jobs"
        path="/jobs/:id?"
        component={Jobs}
      /> : null}
      <DashboardLayout
        title="Military Crosswalk"
        path="/military-crosswalk"
        component={MilitaryCrosswalk}
      />
      <Route path="/loading" component={FullPageLoading} />
      <DashboardLayout title="Oops" path="/error" component={ErrorPage} />
      <Route path="/change-view" component={ChangeView} />
      <Route path="/intro" component={Intro} />
      <Route path="/more-info" component={MoreInfoPage} />
      <DashboardLayout path="/api-docs" component={ApiDocs} />
      <DashboardLayout
        component={Page404}
        title="Not Found"
      />
    </Switch>
    {/* <Intercom /> */}
    <Alert />
    <PromptModal />
    <Snackbar />
    {isLoggedIn && user && <SupportWidget />}
    <Fill name="Dashboard">
      <TermsAndConditionsNotification />
      <PositionTypeMissing />
      {connectMode ? <ConnectWithCollegeModal /> : null}
      <MergeRequestModal />
      <ExtraUserInformation />
      {companyDomain === 'clcillinois' ? <ExtraUserInformationLakeCounty /> : null}
      {companyDomain === 'nwtc' ? <ExtraUserInformationNwtc /> : null}
      <NewPersonalityAssessmentModal />
      <Slot name="modal">
        {slots => slots[0] || null}
      </Slot>
    </Fill>
    <GoogleAnalytics />
    {companyDomain === 'ubeu' ? <UbeuLeadIdScript /> : null}
    {showBanner ? <MaintenanceNotification /> : null}
  </Provider>);
};

App.propTypes = {
  level: PropTypes.number,
  jobBoard: PropTypes.bool,
  showMyJourney: PropTypes.bool,
  connectMode: PropTypes.bool,
  customPrograms: PropTypes.bool,
  view: PropTypes.string,
  terms: PropTypes.string,
  companyDomain: PropTypes.string,
  siteMode: PropTypes.number,
  showBanner: PropTypes.bool,
  setWindowDimensions: PropTypes.func,
  isLoggedIn: PropTypes.bool,
  companyId: PropTypes.number,
  user: PropTypes.object,
};

export default compose(
  withRouter,
  connect(({
    theme: {
      jobBoard,
      terms,
      showMyJourney,
      siteMode,
      customPrograms,
      companyDomain,
      connectMode,
      companyID_FK: companyId,
    },
    app: { view, loading },
    auth,
    me: user,
  }) => ({
    view,
    jobBoard,
    terms,
    siteMode,
    customPrograms,
    level: auth.level,
    showBanner: isAuthenticated(auth) && auth.bannerFlag === '1',
    showMyJourney,
    companyDomain,
    loading,
    connectMode,
    isLoggedIn: isAuthenticated(auth),
    companyId,
    user,
  }), {
    syncLoggedInUser: syncLoggedInUserAction,
    changeView: changeViewAction,
    getBannerFlag: getBannerFlagAction,
    getBannerMessage: getBannerMessageAction,
    setWindowDimensions: setWindowDimensionsAction,
    setPGHomeLink: setPGHomeLinkAction,
  }),
  spinnerWhileLoading(({ loading }) => loading),
  lifecycle({
    componentWillReceiveProps(nextProps) {
      const nextPathname = nextProps.location.pathname;
      const preview = nextProps.location.search === '?preview';
      const { location: { pathname }, view, changeView } = this.props;
      if (
        nextPathname !== pathname &&
                studentRoutes.some(route => nextPathname.indexOf(route) !== -1) &&
                view === COUNSELOR_VIEW &&
                !preview
      ) {
        changeView(STUDENT_VIEW);
      }
    },
    componentWillMount() {
      const { pathname, search } = this.props.location;
      const { view, changeView } = this.props;
      if (
        studentRoutes.some(route => pathname.indexOf(route) !== -1) &&
                view === COUNSELOR_VIEW &&
                search !== '?preview'
      ) {
        changeView(STUDENT_VIEW);
      }
    },
    componentDidMount() {
      sEmitter.on('user-sync', (payload) => {
        this.props.syncLoggedInUser(payload);
      });
      const {
        getBannerFlag,
        getBannerMessage,
        location,
        history,
        setWindowDimensions,
        setPGHomeLink,
      } = this.props;
      const searchParams = new URLSearchParams(location.search);
      getBannerFlag();
      getBannerMessage();
      setWindowDimensions({
        height: window.innerHeight,
        width: window.innerWidth,
        orientation: window.orientation,
      });
      setPGHomeLink(searchParams.get('pg_home_link'));
      if (searchParams.get('pg_home_link')) {
        history.replace(location.pathname);
      }
    },
  }),
)(App);



// WEBPACK FOOTER //
// ./src/client/App.js